import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Copy, Check, Users, Gift, Link2, Loader, Share2, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import './ReferralPage.css';

function ReferralPage() {
  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    fetchReferralInfo();
  }, []);

  const fetchReferralInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users/referral-info');
      setReferralInfo(response.data);
    } catch (error) {
      console.error('Error fetching referral info:', error);
      toast.error('Ошибка загрузки информации о реферальной программе');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, isCode = false) => {
    try {
      await navigator.clipboard.writeText(text);
      if (isCode) {
        setCopiedCode(true);
        toast.success('Скопирован код!');
        setTimeout(() => setCopiedCode(false), 2000);
      } else {
        setCopied(true);
        toast.success('Ссылка скопирована!');
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Ошибка копирования');
    }
  };

  const shareLink = async () => {
    const link = referralInfo?.referral_link || '';
    const shareText = `Присоединяйся к PIKLY! Попробуй PIKLY с моей реферальной ссылкой: ${link}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Присоединяйся к PIKLY!',
          text: shareText,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Если Web Share API не работает, копируем текст с ссылкой
          try {
            await navigator.clipboard.writeText(shareText);
            toast.success('Текст с ссылкой скопирован!');
          } catch (clipboardError) {
            copyToClipboard(link);
          }
        }
      }
    } else {
      // Если Web Share API не поддерживается, копируем текст с ссылкой
      try {
        await navigator.clipboard.writeText(shareText);
        toast.success('Текст с ссылкой скопирован!');
      } catch (clipboardError) {
        copyToClipboard(link);
      }
    }
  };

  if (loading) {
    return (
      <div className="referral-page">
        <div className="referral-loading">
          <Loader className="spinner" size={32} />
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="referral-page">
      <div className="referral-hero">
        <div className="referral-hero-content">
          <div className="referral-badge">
            <Gift size={16} />
            Реферальная программа
          </div>
          <h1>Приглашайте друзей и получайте токены</h1>
          <p className="referral-hero-subtitle">
            Когда реферал совершит первую покупку, вы получите {referralInfo?.bonus_percent || 5}% от суммы в качестве токенов
          </p>
        </div>
      </div>

      <div className="referral-content">
        <div className="referral-main-card">
          <div className="referral-card-header">
            <Link2 size={24} />
            <div>
              <h2>Ваша реферальная ссылка</h2>
              <p className="referral-card-description">Поделитесь этой ссылкой с друзьями</p>
            </div>
          </div>

          <div className="referral-link-container">
            <input
              type="text"
              value={referralInfo?.referral_link || ''}
              readOnly
              className="referral-link-input"
            />
            <div className="referral-link-actions">
              <button
                className="referral-action-btn copy-btn"
                onClick={() => copyToClipboard(referralInfo?.referral_link, false)}
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
                {copied ? 'Скопировано' : 'Копировать'}
              </button>
              <button
                className="referral-action-btn share-btn"
                onClick={shareLink}
              >
                <Share2 size={18} />
                Поделиться
              </button>
            </div>
          </div>

          <div className="referral-code-display">
            <span className="referral-code-label">Реферальный код:</span>
            <div className="referral-code-badge">
              <span className="referral-code">{referralInfo?.referral_code}</span>
              <button
                className="copy-code-btn"
                onClick={() => copyToClipboard(referralInfo?.referral_code, true)}
                title="Копировать код"
              >
                {copiedCode ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </div>
        </div>

        <div className="referral-stats-grid">
          <div className="referral-stat-card">
            <div className="referral-stat-icon users-stat">
              <Users size={32} />
            </div>
            <div className="referral-stat-content">
              <div className="referral-stat-value">{referralInfo?.referrals_count || 0}</div>
              <div className="referral-stat-label">Привлечено рефералов</div>
            </div>
          </div>

          <div className="referral-stat-card">
            <div className="referral-stat-icon gift-stat">
              <TrendingUp size={32} />
            </div>
            <div className="referral-stat-content">
              <div className="referral-stat-value">
                {referralInfo?.total_referral_bonuses
                  ? Math.round(referralInfo.total_referral_bonuses)
                      .toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                  : '0'}
              </div>
              <div className="referral-stat-label">Заработано токенов</div>
            </div>
          </div>
        </div>

        <div className="referral-how-it-works">
          <h3>Как это работает</h3>
          <div className="referral-steps">
            <div className="referral-step">
              <div className="referral-step-number">1</div>
              <div className="referral-step-content">
                <h4>Поделитесь ссылкой</h4>
                <p>Отправьте реферальную ссылку друзьям через соцсети, мессенджеры или email</p>
              </div>
            </div>
            <div className="referral-step">
              <div className="referral-step-number">2</div>
              <div className="referral-step-content">
                <h4>Регистрация друга</h4>
                <p>Ваш друг регистрируется по вашей ссылке и становится вашим рефералом</p>
              </div>
            </div>
            <div className="referral-step">
              <div className="referral-step-number">3</div>
              <div className="referral-step-content">
                <h4>Получайте токены</h4>
                <p>Когда реферал совершит первую покупку, вы получите {referralInfo?.bonus_percent || 5}% от суммы в качестве токенов</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReferralPage;

